import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Url } from '@wix/da-url';
import { PapiGallection } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButton';
import GoToPageIcon from '@wix/da-ds/pkg/Icons/24x24/GoToPage';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import VerticalEllipsis from '@wix/da-ds/pkg/Icons/16x16/VerticalEllipsis';
import { useCopyDeviationOrGallectionLinkClick } from '@wix/da-shared-react/pkg/utils/hooks/useCopyLinkClick';
import CopyLinkButton from '../../../../../tableActions/CopyLinkButton';

import s from './PremiumGalleryActions.scss';

interface Props {
  gallery: PapiGallection;
  className?: string;
}

const PremiumGalleryActions: FC<Props> = ({ gallery, className }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { onCopyLinkClick } = useCopyDeviationOrGallectionLinkClick(undefined, gallery);

  const contextMenuItems = useMemo(
    () => [
      {
        label: t('common.view'),
        link: Url.userGalleryLink({
          username: gallery.owner.username,
          gallectionType: gallery.type,
          folderId: gallery.folderId,
          folderName: gallery.name,
        }),
        target: '_blank',
      },
      {
        label: t('studio.table.actions.copyLink'),
        onClick: onCopyLinkClick,
      },
      {
        label: t('common.manage'),
        link: Url.studioGallery(gallery.folderId),
      },
    ],
    [gallery, onCopyLinkClick, t]
  );

  if (isMobile) {
    return (
      <IconButtonWithContextMenu
        className={className}
        icon={VerticalEllipsis}
        size="medium"
        variant="main"
        aria-label={t('common.more')}
        items={contextMenuItems}
      />
    );
  }

  return (
    <div className={cn(s['root'], className)}>
      <IconButton
        className={s['button']}
        icon={GoToPageIcon}
        aria-label={t('common.view')}
        size="medium"
        href={Url.userGalleryLink({
          username: gallery.owner.username,
          gallectionType: gallery.type,
          folderId: gallery.folderId,
          folderName: gallery.name,
        })}
        target="_blank"
      />

      <CopyLinkButton className={s['button']} gallection={gallery} variant="icon" />
      <GhostButton
        className={s['button']}
        variant="main"
        size="medium"
        rounded
        href={Url.studioGallery(gallery.folderId)}
      >
        {t('common.manage')}
      </GhostButton>
      <div className={s['context-menu']}>
        <IconButtonWithContextMenu
          placement={'bottom-start'}
          items={contextMenuItems}
          size="medium"
          variant="main"
          icon={EllipsisIcon}
          aria-label={t('siteHeader.more')}
          onClick={e => e.stopPropagation()}
          title={t('common.more')}
          standardMenuProps={{ usePortal: false } as any}
        />
      </div>
    </div>
  );
};
PremiumGalleryActions.displayName = 'PremiumGalleryActions';

export default PremiumGalleryActions;
